import type { GlobalState } from '@affine/core/modules/storage';
import {
  storeGlobalPreference,
  watchGlobalPreference,
} from '@affine/core/modules/streamlain/shared/global-preference';
import {
  STREAMLAIN_AI_PANEL_MIN_HEIGHT,
  STREAMLAIN_AI_PANEL_MIN_WIDTH,
  type StreamlainAIIslandSnapPoint,
} from '@affine/core/modules/streamlain/shared/workspace-pane-layout';

const PANEL_SIZE_KEY = 'streamlain.aiPanel.size';
/** A phone-sized view's panel spans the view: only its height is the user's, kept apart. */
const PHONE_PANEL_SIZE_KEY = 'streamlain.aiPanel.phoneSize';

export interface AIPanelSize {
  width: number;
  height: number;
}

/** The panel edges the user can drag; the bottom edge stays where the dock row's is. */
export type AIPanelEdge = 'top' | 'left' | 'right' | 'top-left' | 'top-right';

const EDGES_BY_SNAP: Record<StreamlainAIIslandSnapPoint, AIPanelEdge[]> = {
  // Anchored at its right end above a right island: it grows to the left and up.
  'bottom-right': ['top', 'left', 'top-left'],
  'bottom-left': ['top', 'right', 'top-right'],
  // Centred above a centred island: it grows to both sides and up.
  'bottom-center': ['top', 'left', 'right', 'top-left', 'top-right'],
};

/** The edges that resize a panel opened at `snap`; a phone-sized view's only by its top. */
export function aiPanelResizeEdges(
  snap: StreamlainAIIslandSnapPoint,
  phone: boolean
): AIPanelEdge[] {
  return phone ? ['top'] : EDGES_BY_SNAP[snap];
}

/** A stored size, or null (the default size) for none or a malformed value. */
export function parseAIPanelSize(value: unknown): AIPanelSize | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const { width, height } = value as Partial<
    Record<keyof AIPanelSize, unknown>
  >;
  if (
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { width, height };
}

const sizeKey = (phone: boolean) =>
  phone ? PHONE_PANEL_SIZE_KEY : PANEL_SIZE_KEY;

/** The user's panel size in a phone-sized view or a wider one; null until they resize it. */
export function aiPanelSize$(globalState: GlobalState, phone: boolean) {
  return watchGlobalPreference(globalState, sizeKey(phone), parseAIPanelSize);
}

export function storeAIPanelSize(
  globalState: GlobalState,
  size: AIPanelSize,
  phone: boolean
) {
  storeGlobalPreference(globalState, sizeKey(phone), size);
}

/**
 * The panel's size while the user drags `edge` by (dx, dy) from `start`. A centred panel grows on both
 * sides, so its horizontal edges count twice. The view clamps the rendered panel; this keeps it
 * above the least usable size.
 */
export function resizeAIPanel(
  start: AIPanelSize,
  edge: AIPanelEdge,
  snap: StreamlainAIIslandSnapPoint,
  delta: { dx: number; dy: number }
): AIPanelSize {
  const sides = snap === 'bottom-center' ? 2 : 1;
  const horizontal = edge.endsWith('left')
    ? -delta.dx
    : edge.endsWith('right')
      ? delta.dx
      : 0;
  const vertical = edge.startsWith('top') ? -delta.dy : 0;
  return {
    width: Math.max(
      STREAMLAIN_AI_PANEL_MIN_WIDTH,
      start.width + horizontal * sides
    ),
    height: Math.max(STREAMLAIN_AI_PANEL_MIN_HEIGHT, start.height + vertical),
  };
}
